/**
 * useScrollRestore — saves and restores scroll position per route.
 *
 * When the user navigates away from a view, the scroll position is saved
 * in an in-memory Map keyed on the URL pathname. When they navigate back,
 * the position is restored after a short delay (to let the virtualizer
 * measure items).
 *
 * Strategy:
 * - On route change, save the current scroll position for the OLD pathname
 * - After new content mounts, restore saved position (if any) for the NEW pathname
 * - The Map is never persisted — cleared on full page reload (acceptable UX)
 * - On IDB crash reload: reads persisted scroll from localStorage (one-shot)
 *
 * @see §18 Fas 18 — Scroll position cache per vy
 * @see handleIndexedDBError.ts — saves recovery scroll before reload
 */

import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"

/** localStorage key written by handleIndexedDBError before reload. */
const RECOVERY_SCROLL_KEY = "totodo_recovery_scroll"

/** Module-level scroll cache — survives component re-mounts. */
const scrollCache = new Map<string, number>()

/** Read the cached scroll position for a given pathname (0 if none). */
export function getSavedScroll(pathname: string): number {
  return scrollCache.get(pathname) ?? 0
}

/** Delay (ms) before restoring scroll. Gives virtualizer time to measure. */
const RESTORE_DELAY_MS = 50

// Hydrate from crash-recovery localStorage (one-shot at module load)
try {
  const raw = localStorage.getItem(RECOVERY_SCROLL_KEY)
  if (raw) {
    const { pathname, scrollTop } = JSON.parse(raw) as {
      pathname: string
      scrollTop: number
    }
    if (pathname && scrollTop > 0) {
      scrollCache.set(pathname, scrollTop)
    }
    localStorage.removeItem(RECOVERY_SCROLL_KEY)
  }
} catch {
  // Best effort
}

/**
 * Saves scroll position on unmount/navigation; restores on mount.
 *
 * @param scrollRef - Ref to the scrollable container element (<main>)
 */
export function useScrollRestore(
  scrollRef: React.RefObject<HTMLElement | null>,
): void {
  const { pathname } = useLocation()
  const prevPathnameRef = useRef(pathname)

  // Continuously track scroll position so the cached value is always fresh.
  // This avoids reading scrollTop after React has already swapped view content
  // (which would give us the wrong value).
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handler = () => {
      scrollCache.set(pathname, el.scrollTop)
    }
    el.addEventListener("scroll", handler, { passive: true })
    return () => el.removeEventListener("scroll", handler)
  }, [pathname, scrollRef])

  // Restore scroll position after mounting new view content.
  // If no cached position exists we reset to 0 so the shared <main>
  // element doesn't carry over scroll from the previous route.
  useEffect(() => {
    if (prevPathnameRef.current === pathname) return
    prevPathnameRef.current = pathname

    const savedPosition = scrollCache.get(pathname) ?? 0

    const el = scrollRef.current
    if (!el) return

    // Immediate reset — prevents flicker of old scroll position
    el.scrollTop = savedPosition

    // Also set after a short delay for virtualizer-rendered lists
    const raf = requestAnimationFrame(() => {
      const timer = setTimeout(() => {
        el.scrollTop = savedPosition
      }, RESTORE_DELAY_MS)
      // Clean up the timeout if we navigate away quickly
      return () => clearTimeout(timer)
    })

    return () => cancelAnimationFrame(raf)
  }, [pathname, scrollRef])

  // Save on unmount (component-level cleanup)
  useEffect(() => {
    return () => {
      const el = scrollRef.current
      if (el) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        scrollCache.set(pathname, el.scrollTop)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
